import { useState, useEffect, useRef, useMemo } from "react";
import { SearchInput } from "../components/search/SearchInput";
import { Brain, ChevronLeft, ChevronRight } from "lucide-react";
import { SearchHeader } from "../components/SearchHeader";
import { useRecordsApi } from "../lib/api/records";
import { useSearchParams } from "react-router-dom";
import { RagResultCard } from "../components/rag/RagResultCard";
import { RagConfigToggle } from "../components/rag/RagConfigToggle";
import type { DocumentRecord } from "../types/record";
import { appConfig } from "../config/appConfig";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import type { DeltaStatic } from "quill";

import intelligentiaLogo from "../images/intelligentiahumana.jpg";

// Define the RagSource interface matching the API response
interface RagSource {
  id: string;
  title?: string;
  score: number;
  content?: string;
  formatted_content?: DeltaStatic;
}

// Define the RagResponse interface matching the API response
interface RagResponse {
  text: string;
  sources: RagSource[];
  search_id?: number;
  execution_time_ms: number;
  error?: string;
}

// -----------------------------
// Markdown Renderer
// -----------------------------
function MarkdownRenderer({ content }: { content: string }) {
  const elements = useMemo(() => {
    if (!content) return [];

    // Debug: Log the raw content to see what we're receiving
    console.log("MarkdownRenderer received content:", JSON.stringify(content));
    console.log("Content length:", content.length);

    // Trim content to remove leading/trailing whitespace and empty lines
    const trimmedContent = content.trim();
    if (!trimmedContent) return [];

    // Split on single newline
    const lines = trimmedContent.split("\n");
    console.log(
      "Lines after split:",
      lines.map((line, i) => `${i}: "${line}"`)
    );

    const elements: JSX.Element[] = [];
    let currentParagraph: string[] = [];
    let hasSeenContent = false;

    const flushCurrentParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join("\n").trim();
        if (paragraphText) {
          elements.push(
            <p
              key={elements.length}
              className="text-left mb-1 whitespace-pre-wrap"
            >
              {renderTextWithFormatting(paragraphText)}
            </p>
          );
        }
        currentParagraph = [];
      }
    };

    // Helper function to render text with bold formatting
    const renderTextWithFormatting = (text: string) => {
      // Split text by **bold** markers
      const parts = text.split(/(\*\*.*?\*\*)/g);

      return parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          // Bold text
          const boldText = part.slice(2, -2);
          return (
            <strong key={index} className="font-bold">
              {boldText}
            </strong>
          );
        }
        return part;
      });
    };

    // Helper function to check if a line is a numbered list item
    const isNumberedListItem = (line: string): boolean => {
      return /^\s*\d+\.\s/.test(line);
    };

    // Helper function to check if a line contains multiple numbered items
    const hasMultipleNumberedItems = (line: string): boolean => {
      // Match patterns like "1. 2. 3. 4. 5." or "1. item 2. item 3. item"
      const matches = line.match(/\d+\./g);
      return matches !== null && matches.length > 1;
    };

    // Helper function to parse and format multiple numbered items
    const parseMultipleNumberedItems = (line: string): JSX.Element => {
      // Split by number patterns while keeping the numbers
      const parts = line.split(/(\d+\.)/);
      const items: JSX.Element[] = [];

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        if (/^\d+\.$/.test(part)) {
          // This is a number
          const nextPart = parts[i + 1] || "";
          const content = nextPart.trim();

          items.push(
            <div key={i} className="text-left mb-1 pl-6">
              <span className="font-medium">{part}</span>{" "}
              <span>{renderTextWithFormatting(content)}</span>
            </div>
          );
          i++; // Skip the next part since we already processed it
        }
      }

      return <div key={elements.length}>{items}</div>;
    };

    // Helper function to check if a line contains a colon-separated list
    const hasColonSeparatedList = (line: string): boolean => {
      // Match patterns like "Items: item1; item2; item3"
      return line.includes(":") && line.includes(";");
    };

    // Helper function to parse and format colon-separated lists
    const parseColonSeparatedList = (line: string): JSX.Element => {
      const colonIndex = line.indexOf(":");
      const prefix = line.substring(0, colonIndex + 1).trim();
      const listContent = line.substring(colonIndex + 1).trim();

      // Split by semicolons and clean up each item
      const items = listContent
        .split(";")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      const listElements: JSX.Element[] = [];

      // Add the prefix if it exists
      if (prefix && prefix !== ":") {
        listElements.push(
          <div key="prefix" className="text-left mb-2 font-medium">
            {renderTextWithFormatting(prefix)}
          </div>
        );
      }

      // Add each list item
      items.forEach((item, index) => {
        listElements.push(
          <div key={`item-${index}`} className="text-left mb-1 pl-6">
            <span className="font-medium">•</span>{" "}
            <span>{renderTextWithFormatting(item)}</span>
          </div>
        );
      });

      return <div key={elements.length}>{listElements}</div>;
    };

    for (const line of lines) {
      const trimmedLine = line.trim();

      if (trimmedLine === "") {
        // Empty line - end current paragraph and add spacing if we have content
        flushCurrentParagraph();
        if (hasSeenContent) {
          elements.push(
            <div key={elements.length} className="mb-2">
              &nbsp;
            </div>
          );
        }
      } else if (hasColonSeparatedList(trimmedLine)) {
        // Colon-separated list - flush any current paragraph first
        flushCurrentParagraph();
        elements.push(parseColonSeparatedList(trimmedLine));
        hasSeenContent = true;
      } else if (hasMultipleNumberedItems(trimmedLine)) {
        // Multiple numbered items on same line - flush any current paragraph first
        flushCurrentParagraph();
        elements.push(parseMultipleNumberedItems(trimmedLine));
        hasSeenContent = true;
      } else if (isNumberedListItem(trimmedLine)) {
        // Single numbered list item - flush any current paragraph first
        flushCurrentParagraph();

        // Extract number and content
        const match = trimmedLine.match(/^(\d+)\.\s(.*)$/);
        if (match) {
          const [, number, itemContent] = match;
          elements.push(
            <div key={elements.length} className="text-left mb-1 pl-6">
              <span className="font-medium">{number}.</span>{" "}
              <span>{renderTextWithFormatting(itemContent)}</span>
            </div>
          );
        }
        hasSeenContent = true;
      } else {
        // Regular content line
        currentParagraph.push(line);
        hasSeenContent = true;
      }
    }

    // Flush any remaining paragraph
    flushCurrentParagraph();

    return elements;
  }, [content]);

  return <div className="markdown-content">{elements}</div>;
}

export function RagSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const recordsApi = useRecordsApi();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const [ragResponse, setRagResponse] = useState<RagResponse | null>(null);
  const [thinkingTime, setThinkingTime] = useState<number>(0);
  const thinkingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<RagSource | null>(
    null
  );
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);

  // Handle search and RAG in a single call
  const handleSearch = async (newQuery: string) => {
    setError(null);
    setRagResponse(null);
    setSearchParams({ q: newQuery });
    setSearchKey((prev) => prev + 1);
    setSelectedDocument(null);

    // Cancel any ongoing RAG generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (newQuery.trim()) {
      setIsLoading(true);
      startThinkingTimer();

      try {
        if (appConfig.rag.useStreaming) {
          // Handle streaming case
          abortControllerRef.current = new AbortController();
          await handleStreamingRag(newQuery);
        } else {
          // Handle non-streaming case
          await handleSingleCallRag(newQuery);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("RAG error:", error);
          setError(
            error instanceof Error
              ? error.message
              : "An error occurred while generating the response"
          );
        }
      } finally {
        setIsLoading(false);
        stopThinkingTimer();
      }
    }
  };

  // Handle single call RAG (non-streaming)
  const handleSingleCallRag = async (query: string) => {
    try {
      const response = await recordsApi.generateRagResponse(
        query,
        undefined,
        appConfig.rag.defaultContextLimit
      );

      // If the response is already a RagResponse object, use it directly
      if (typeof response === "object" && response.text && response.sources) {
        setRagResponse(response as RagResponse);
      } else if (typeof response === "string") {
        // Legacy case - convert string response to RagResponse format
        setRagResponse({
          text: response,
          sources: [],
          execution_time_ms: 0,
        });
      }
    } catch (error) {
      handleRagError(error);
    }
  };

  // Handle streaming RAG
  const handleStreamingRag = async (query: string) => {
    try {
      let responseText = "";
      let sources: RagSource[] = [];
      let foundSourcesJson = false;

      // Callback for processing stream chunks
      const handleChunk = (chunk: string) => {
        // Debug: Log received chunks
        console.log("handleChunk received:", {
          chunk: chunk,
          repr: JSON.stringify(chunk),
          length: chunk.length,
          isEmpty: chunk === "",
          isWhitespace: chunk.trim() === "",
        });

        // Check if chunk contains sources data
        if (chunk.includes("__SOURCES__")) {
          foundSourcesJson = true;
          const sourcesJsonStr = chunk.split("__SOURCES__")[1].trim();
          try {
            const sourcesInfo = JSON.parse(sourcesJsonStr);
            console.log("Received sources info:", sourcesInfo);
            if (sourcesInfo.sources) {
              sources = sourcesInfo.sources;
            }
            // Don't add this chunk to the displayed text
            return;
          } catch (e) {
            console.error("Failed to parse sources JSON:", e);
          }
        }

        // Add chunk to response text if it's not the sources marker
        if (!foundSourcesJson) {
          // Skip empty chunks at the beginning - only count them as newlines if there's already content
          if (chunk === "" || chunk.trim() === "") {
            // Only add newlines if we already have content
            if (responseText.trim() !== "") {
              responseText += "\n\n";
            }
            // Skip empty chunks at the start
          } else {
            responseText += chunk;
          }

          // Update the response in real-time
          setRagResponse((prev) => ({
            text: responseText,
            sources: prev?.sources || [],
            execution_time_ms: prev?.execution_time_ms || 0,
          }));
        }
      };

      // Call streaming API
      await recordsApi.generateRagResponse(
        query,
        handleChunk,
        appConfig.rag.defaultContextLimit
      );

      // After streaming completes, set the final response with sources
      setRagResponse({
        text: responseText,
        sources: sources,
        execution_time_ms: thinkingTime * 1000,
      });
    } catch (error) {
      handleRagError(error);
    }
  };

  // Handle RAG error
  const handleRagError = (error: any) => {
    if (error instanceof Error && error.name !== "AbortError") {
      console.error("RAG generation error:", error);
      setError(
        error instanceof Error
          ? error.message
          : "An error occurred while generating the response"
      );

      // If we have no response yet, set a default error message
      if (!ragResponse) {
        setRagResponse({
          text: "Je suis désolé, une erreur s'est produite. Veuillez réessayer plus tard.",
          sources: [],
          execution_time_ms: 0,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    }
  };

  // Start thinking timer
  const startThinkingTimer = () => {
    setThinkingTime(0);
    thinkingTimerRef.current = setInterval(() => {
      setThinkingTime((prev) => prev + 0.01);
    }, 10);
  };

  // Stop thinking timer
  const stopThinkingTimer = () => {
    if (thinkingTimerRef.current) {
      clearInterval(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
  };

  // Check if the response indicates no information was found
  const isNoInformationResponse = (response: string | undefined): boolean => {
    if (!response) return false;

    const noInfoPhrases = [
      "ne contient pas d'information",
      "don't have information",
      "no information",
      "pas d'information",
      "aucune information",
      "je ne sais pas",
      "je ne peux pas répondre",
    ];

    return noInfoPhrases.some((phrase) =>
      response.toLowerCase().includes(phrase.toLowerCase())
    );
  };

  // Format thinking time as MM.SS or MM.SS.mm depending on if generation is complete
  const formatThinkingTime = (
    time: number,
    showMilliseconds: boolean = false
  ): string => {
    const minutes = Math.floor(time / 60);

    if (showMilliseconds) {
      // Show milliseconds for the final time display (MM.SS.mm)
      const seconds = (time % 60).toFixed(2).padStart(5, "0");
      return `${minutes.toString().padStart(2, "0")}.${seconds}`;
    } else {
      // Only show seconds while thinking (MM.SS)
      const seconds = Math.floor(time % 60)
        .toString()
        .padStart(2, "0");
      return `${minutes.toString().padStart(2, "0")}.${seconds}`;
    }
  };

  // Cancel RAG generation when component unmounts
  useEffect(() => {
    return () => {
      stopThinkingTimer();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle document selection
  const handleDocumentClick = async (document: RagSource) => {
    // If the clicked document is already selected, close it
    if (selectedDocument && selectedDocument.id === document.id) {
      setSelectedDocument(null);
      return;
    }

    try {
      // First set the document with basic information to show loading state
      setIsDocumentLoading(true);
      setSelectedDocument({
        ...document,
        content: "Loading content...",
      });

      // Fetch the full document content
      const fullContent = await recordsApi.getDocumentContent(document.id);

      // Update the selected document with the full content
      setSelectedDocument({
        ...document,
        content: fullContent.content,
        formatted_content: fullContent.formatted_content,
      });
    } catch (error) {
      console.error("Error fetching document content:", error);
      // Still set the document but with an error message
      setSelectedDocument({
        ...document,
        content: "Error loading content. Please try again.",
      });
    } finally {
      setIsDocumentLoading(false);
    }
  };

  // Navigate to previous document
  const navigateToPrevDocument = async () => {
    if (!selectedDocument || !ragResponse || !ragResponse.sources.length)
      return;

    const currentIndex = ragResponse.sources.findIndex(
      (doc) => doc.id === selectedDocument.id
    );
    if (currentIndex > 0) {
      // Get the previous document
      const prevDocument = ragResponse.sources[currentIndex - 1];
      // Fetch its content
      await handleDocumentClick(prevDocument);
    }
  };

  // Navigate to next document
  const navigateToNextDocument = async () => {
    if (!selectedDocument || !ragResponse || !ragResponse.sources.length)
      return;

    const currentIndex = ragResponse.sources.findIndex(
      (doc) => doc.id === selectedDocument.id
    );
    if (currentIndex < ragResponse.sources.length - 1) {
      // Get the next document
      const nextDocument = ragResponse.sources[currentIndex + 1];
      // Fetch its content
      await handleDocumentClick(nextDocument);
    }
  };

  // Close document view
  const closeDocumentView = () => {
    setSelectedDocument(null);
  };

  // Handle keyboard navigation for document preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedDocument) return;

      if (e.key === "ArrowLeft") {
        navigateToPrevDocument();
      } else if (e.key === "ArrowRight") {
        navigateToNextDocument();
      } else if (e.key === "Escape") {
        closeDocumentView();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedDocument, ragResponse]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-brand-bg">
      <SearchHeader />
      <div className="relative w-full max-w-7xl px-4">
        <div className="flex flex-col items-center">
          <img
            src={intelligentiaLogo}
            alt="Intelligentia Logo"
            className="w-80 object-contain"
          />

          <p className="mb-8 text-lg text-brand-text text-center">
            Accéder à l'intelligence humaine propulsée par l'IA
          </p>

          <div className="relative flex w-full justify-center">
            <div className="w-full max-w-2xl">
              <SearchInput onSearch={handleSearch} defaultValue={query} />

              <div className="flex justify-end mt-2">
                <RagConfigToggle />
              </div>

              {isLoading && (
                <div className="mt-8 text-brand-text text-left">
                  <div className="flex items-center gap-4">
                    <div className="animate-pulse">
                      Réflexion - {formatThinkingTime(thinkingTime)} secondes
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-8 rounded-xl bg-brand-error/10 p-4 text-brand-text text-left">
                  {error}
                </div>
              )}

              {/* Show source documents */}
              {ragResponse &&
                ragResponse.sources &&
                ragResponse.sources.length > 0 && (
                  <div className="mt-8 w-full">
                    <div className="flex gap-2">
                      {ragResponse.sources
                        .slice(0, ragResponse.sources.length > 4 ? 3 : 4)
                        .map((doc) => (
                          <div key={doc.id} className="w-1/4">
                            <RagResultCard
                              document={{
                                id: doc.id,
                                title: doc.title || "Untitled Document",
                                content: doc.content || "",
                                score: doc.score,
                              }}
                              onClick={() => handleDocumentClick(doc)}
                            />
                          </div>
                        ))}
                      {ragResponse.sources.length > 4 && (
                        <div className="w-1/4">
                          <RagResultCard
                            remainingCount={ragResponse.sources.length - 3}
                            onClick={() => {
                              // Show the first unshown document when clicking "more"
                              if (ragResponse.sources.length > 3) {
                                handleDocumentClick(ragResponse.sources[3]);
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Document Preview - In-line expanded view */}
              {selectedDocument && (
                <div
                  className="mt-8 w-full rounded-xl bg-brand-bg p-6 shadow-card animate-slideDown origin-top relative"
                  style={{ minHeight: "600px" }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-l font-bold text-black max-w-[85%] overflow-hidden">
                      {selectedDocument.title || "Untitled Document"}
                    </h2>
                    <button
                      className="text-black/70 hover:text-black/90 w-8 h-8 flex items-center justify-center transition-colors flex-shrink-0"
                      onClick={closeDocumentView}
                      aria-label="Close document preview"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="absolute top-3 right-12 text-[10px] text-black/50 px-2 py-1 rounded-md bg-brand-surface">
                    <div className="flex flex-col">
                      <div className="flex items-center mb-1">
                        <span>← →: Navigate documents</span>
                      </div>
                      <div className="flex items-center">
                        <span>ESC: Close preview</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-sm text-black/70 mb-4 border-b border-black/10 pb-4">
                    <div>Document ID: {selectedDocument.id}</div>
                    {selectedDocument.score !== undefined && (
                      <div className="font-semibold bg-brand-surface rounded-lg px-3 py-1 text-black/90 flex items-center">
                        <span className="mr-1">Relevance:</span>
                        <span className="font-bold text-black">
                          {(selectedDocument.score * 100).toFixed(1)}%
                        </span>
                        <div className="ml-2 h-2 w-16 bg-brand-surface rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-primary"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.round(selectedDocument.score * 100)
                              )}%`,
                              opacity: Math.max(0.5, selectedDocument.score),
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div
                    className="flex flex-col"
                    style={{ height: "calc(600px - 160px)" }}
                  >
                    <div
                      className="prose max-w-none whitespace-pre-wrap text-left text-black overflow-auto transition-all flex-grow"
                      style={{ height: "calc(100% - 60px)" }}
                    >
                      {selectedDocument.content === "Loading content..." ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-2"></div>
                          <p className="text-black/70">
                            Loading document content...
                          </p>
                        </div>
                      ) : selectedDocument.content ===
                        "Error loading content. Please try again." ? (
                        <div className="flex flex-col items-center justify-center h-full">
                          <div className="bg-brand-error/10 p-4 rounded-lg text-brand-error mb-4">
                            <p className="font-bold">Error loading content</p>
                            <p className="text-sm">
                              There was a problem retrieving the document
                              content.
                            </p>
                            <button
                              className="mt-2 px-3 py-1 bg-brand-error text-white rounded-md text-sm hover:opacity-90"
                              onClick={() =>
                                handleDocumentClick(selectedDocument)
                              }
                            >
                              Try Again
                            </button>
                          </div>
                        </div>
                      ) : selectedDocument.formatted_content ? (
                        <ReactQuill
                          ref={quillRef}
                          value={selectedDocument.formatted_content}
                          readOnly
                          theme="bubble"
                          modules={{ toolbar: false }}
                        />
                      ) : (
                        selectedDocument.content || "No content available"
                      )}
                    </div>

                    {/* Document navigation controls - Moved to bottom */}
                    {ragResponse && ragResponse.sources && (
                      <div className="flex justify-center items-center mt-4 pt-3 border-t border-black/10 h-[60px]">
                        <div className="flex items-center">
                          <button
                            className={`px-3 py-1 flex items-center justify-center transition-colors text-black/60 hover:text-black/90 ${
                              ragResponse.sources.findIndex(
                                (doc) => doc.id === selectedDocument.id
                              ) > 0
                                ? ""
                                : "opacity-30 cursor-not-allowed"
                            }`}
                            onClick={navigateToPrevDocument}
                            disabled={
                              isDocumentLoading ||
                              ragResponse.sources.findIndex(
                                (doc) => doc.id === selectedDocument.id
                              ) === 0
                            }
                            aria-label="Previous document"
                          >
                            <ChevronLeft className="w-5 h-5" />
                            <span className="text-xs ml-1">
                              {isDocumentLoading ? "Loading..." : "Previous"}
                            </span>
                          </button>

                          <div className="mx-3 text-xs text-black/50">
                            {ragResponse.sources.findIndex(
                              (doc) => doc.id === selectedDocument.id
                            ) + 1}{" "}
                            of {ragResponse.sources.length}
                          </div>

                          <button
                            className={`px-3 py-1 flex items-center justify-center transition-colors text-black/60 hover:text-black/90 ${
                              ragResponse.sources.findIndex(
                                (doc) => doc.id === selectedDocument.id
                              ) <
                              ragResponse.sources.length - 1
                                ? ""
                                : "opacity-30 cursor-not-allowed"
                            }`}
                            onClick={navigateToNextDocument}
                            disabled={
                              isDocumentLoading ||
                              ragResponse.sources.findIndex(
                                (doc) => doc.id === selectedDocument.id
                              ) ===
                                ragResponse.sources.length - 1
                            }
                            aria-label="Next document"
                          >
                            <span className="text-xs mr-1">
                              {isDocumentLoading ? "Loading..." : "Next"}
                            </span>
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* RAG Response section */}
              {ragResponse && ragResponse.text && (
                <div className="mt-8 w-full rounded-xl bg-brand-bg p-6 shadow-card">
                  <div className="text-lg text-brand-text text-left w-full prose max-w-none">
                    <MarkdownRenderer content={ragResponse.text} />
                    {isLoading && <span className="animate-pulse ml-1">▋</span>}
                  </div>
                </div>
              )}

              {ragResponse && !isLoading && thinkingTime > 0 && (
                <div className="mt-2 text-brand-muted text-sm text-left">
                  {ragResponse.execution_time_ms > 0
                    ? `Generated in ${(
                        ragResponse.execution_time_ms / 1000
                      ).toFixed(2)} seconds`
                    : `Thought for ${formatThinkingTime(
                        thinkingTime,
                        true
                      )} seconds`}
                </div>
              )}

              {query &&
                !isLoading &&
                (!ragResponse || !ragResponse.text) &&
                !error && (
                  <div className="mt-8 text-brand-text text-left">
                    No response received. Try another question.
                  </div>
                )}

              {ragResponse &&
                ragResponse.text &&
                isNoInformationResponse(ragResponse.text) && (
                  <div className="mt-4 text-brand-muted text-sm text-left">
                    No relevant information found in the knowledge base for this
                    query.
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
